import { InjectionToken } from '@angular/core';

/**
 * DI token carrying the detected TTPOI frontend origin (e.g. "https://ttpoi.ghluat.com").
 * Provided in main.embed.ts so components can resolve absolute asset URLs.
 */
export const TTPOI_ORIGIN = new InjectionToken<string>('TTPOI_ORIGIN');

/**
 * Detects the TTPOI frontend origin that served the embed bundle.
 *
 * The embed JS is served from the frontend (e.g.
 * https://ttpoi.ghluat.com/embed/ttpoi-registration-form.js). Static assets
 * (i18n JSON, the global stylesheet) are served from the SAME frontend origin.
 * We detect this at runtime from the script's own URL — no config needed.
 *
 * This deliberately does NOT use API_BASE_URL — the API is a separate service
 * on a different port and does not serve web assets.
 *
 * @returns the origin (e.g. "https://ttpoi.ghluat.com") or "" if undetectable.
 */
export function getTTPoiOrigin(): string {
  // Primary: the currently executing script element's origin
  if (typeof document !== 'undefined' && document.currentScript instanceof HTMLScriptElement) {
    try {
      return new URL(document.currentScript.src).origin;
    } catch {
      // fall through
    }
  }

  // Fallback: scan all script tags for one that matches our bundle filename
  if (typeof document !== 'undefined') {
    const scripts = document.querySelectorAll('script[src*="ttpoi-registration-form"]');
    if (scripts.length > 0) {
      try {
        return new URL((scripts[0] as HTMLScriptElement).src).origin;
      } catch {
        // fall through
      }
    }
  }

  // Last resort: cannot determine origin — relative URLs
  console.warn('[TTPOI] Could not determine embed script origin; assets will use relative paths.');
  return '';
}

/** Filename of the built global stylesheet (see scripts/rename-embed.js). */
export const EMBED_STYLESHEET_FILENAME = 'ttpoi-registration-form.css';

/**
 * Builds the absolute URL of the global stylesheet that must be injected into
 * each widget's shadow root.
 */
export function getEmbedStylesheetUrl(origin: string = getTTPoiOrigin()): string {
  return `${origin}/embed/${EMBED_STYLESHEET_FILENAME}`;
}
